/* eslint-disable @typescript-eslint/naming-convention */
import { takeLatest, put, call, all, select } from 'redux-saga/effects';
import { getFirstError, getErrorMessageFromResponse } from '@adjust/dashboard-core/lib/errorMessage/errorMessage';
import { getUserDetails, postUser, updateUserPermissions } from '@api/user';
import { CustomUserPermissionTabs } from '@react/Pages/UsersPage/UserPermissionSidebar/AppAccess/CustomUserPermissions/utils';
import {
  fetchUser,
  fetchUserSucceed,
  fetchUserFailed,
  saveUserPermissions,
  saveUserPermissionsFailed,
  getUserPermissionsState,
  saveUserPermissionsSuceed,
  createUser,
  createUserSuceed,
  createUserFailed,
  navigateTo,
  saveAppPermissions,
  updateCurrentTab,
  disableSelectedPartners,
  enableSelectedPartners,
  disableSelectedTrackers,
  enableSelectedTrackers,
} from '@redux/ducks/userPermissions/userPermissions';
import { getAccount } from '@redux/ducks/global';
import { createUser as addUserToList, editUser } from '@redux/ducks/users';
import { UserPermissionsScreen } from '@redux/ducks/userPermissions/utils';
import {
  fetchPartnerListWithAccessDetailsSucceed,
  getPartnerState,
  resetState as resetPartnerState,
} from '@redux/ducks/partner/partner';
import {
  fetchTrackerListWithAccessDetailsSucceed,
  getTrackerState,
  resetState as resetTrackerState,
} from '@redux/ducks/tracker/tracker';
import { partnerListWithAccess } from '@redux/ducks/partner/utils';
import { trackerListWithAccess } from '@redux/ducks/tracker/utils';

const errorMessages = {
  user_email_exist_for_account: 'error.user-email-exist-for-account',
};

function getErrorMessage(errorKey) {
  return getFirstError([errorKey], errorMessages);
}

// Workers

export function* fetchUserTask({ payload }) {
  try {
    const account = yield select(getAccount);
    const response = yield call(getUserDetails, { accountId: account.id, id: payload });

    if (!response.local_roles.features) {
      response.local_roles.features = [];
    }
    if (!response.local_roles.mobile_apps) {
      response.local_roles.mobile_apps = [];
    }

    yield put(fetchUserSucceed(response));
  } catch (error) {
    yield put(fetchUserFailed(error));
  }
}

export function* saveUserPermissionsTask() {
  try {
    const { user } = yield select(getUserPermissionsState);
    const account = yield select(getAccount);

    const response = yield call(updateUserPermissions, {
      accountId: account.id,
      id: user.id,
      payload: {
        id: user.id,
        role: user.role,
        local_roles: user.local_roles,
      },
    });

    yield put(saveUserPermissionsSuceed(response));
    yield put(editUser(user));
  } catch (error) {
    yield put(saveUserPermissionsFailed(error));
  }
}

export function* saveUserTask() {
  try {
    const { user } = yield select(getUserPermissionsState);
    const account = yield select(getAccount);

    const response = yield call(postUser, {
      accountId: account.id,
      payload: {
        role: user.role,
        language: 'en',
        email: user.email,
        local_roles: user.local_roles,
        account_id: account.id,
      },
    });

    yield put(createUserSuceed());
    yield put(addUserToList(response));
  } catch (error) {
    const errorMessage = getErrorMessageFromResponse(error.data);
    yield put(createUserFailed(getErrorMessage(errorMessage)));
  }
}

export function* resetStateTask({ payload }) {
  switch (payload) {
    case UserPermissionsScreen.APP_LIST:
      yield put(updateCurrentTab(CustomUserPermissionTabs.Tracker));
      yield put(resetPartnerState());
      yield put(resetTrackerState());
  }
}

export function* resetStateOnSaveAppPermissions() {
  yield put(updateCurrentTab(CustomUserPermissionTabs.Tracker));
  yield put(resetPartnerState());
  yield put(resetTrackerState());
}

export function* updatePartnerListTask() {
  const { partnerList } = yield select(getPartnerState);
  const { currentApp } = yield select(getUserPermissionsState);
  const updatedPartnerList = partnerListWithAccess(partnerList, currentApp.access);

  yield put(fetchPartnerListWithAccessDetailsSucceed([...updatedPartnerList]));
}

export function* updateTrackerListTask() {
  const { trackerList } = yield select(getTrackerState);
  const { currentApp } = yield select(getUserPermissionsState);
  const updatedTrackerList = trackerListWithAccess(trackerList, currentApp.access);

  yield put(fetchTrackerListWithAccessDetailsSucceed([...updatedTrackerList]));
}

// Watchers

export function* watchSaveAppPermissions() {
  yield takeLatest([saveAppPermissions], resetStateOnSaveAppPermissions);
}

export function* watchFetchUser() {
  yield takeLatest([fetchUser], fetchUserTask);
}

export function* watchSaveUserPermissions() {
  yield takeLatest([saveUserPermissions], saveUserPermissionsTask);
}

export function* watchCreateUser() {
  yield takeLatest([createUser], saveUserTask);
}

export function* watchNavigateTo() {
  yield takeLatest([navigateTo], resetStateTask);
}

export function* watchAppPermissionChange() {
  yield takeLatest([disableSelectedPartners, enableSelectedPartners], updatePartnerListTask);
  yield takeLatest([disableSelectedTrackers, enableSelectedTrackers], updateTrackerListTask);
}

// Saga

export default function* userSaga() {
  yield all([
    watchFetchUser(),
    watchSaveUserPermissions(),
    watchCreateUser(),
    watchNavigateTo(),
    watchSaveAppPermissions(),
    watchAppPermissionChange(),
  ]);
}
